import React, { useEffect, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { backofficeUrl } from 'src/shared/helpers';
import routes from 'src/shared/routes';
import { GRAPHQL_URL } from 'src/relay/fetchQuery';
import { getHeaders } from 'src/relay/helpers';
import { Link } from '../buttons';

const STORAGE_KEY_PREFIX = 'zakeke_credits_alert_dismissed_';
const REFRESH_THROTTLE_MS = 30_000;

export const clearCreditsAlertDismiss = () => {
    Object.keys(localStorage)
        .filter((k) => k.startsWith(STORAGE_KEY_PREFIX))
        .forEach((k) => localStorage.removeItem(k));
};

function isDismissed(service: string, currentCredit: number): boolean {
    const stored = localStorage.getItem(STORAGE_KEY_PREFIX + service);
    if (!stored) return false;
    try {
        const parsed = JSON.parse(stored);
        return parsed.credit === currentCredit;
    } catch {
        return false;
    }
}

function dismiss(service: string, currentCredit: number): void {
    localStorage.setItem(STORAGE_KEY_PREFIX + service, JSON.stringify({ credit: currentCredit }));
}

const StrongAlertContent = styled.span`
    //display: inline-block;
    margin: auto;
    font-weight: bold;

    > a {
        color: #0288d1;
        text-decoration: underline;
    }
`;

const AlertOverlay = styled.div<{ $top: number }>`
    position: fixed;
    top: ${(p) => p.$top}px;
    right: 20px;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const AlertBubble = styled.div`
    background-color: #fff0f0;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    padding: 12px 16px;
    max-width: 380px;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.14), 0 8px 24px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.6);
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: auto 1fr;
    color: #333;
    transition: box-shadow 0.2s ease, border-color 0.2s ease;
    animation: blink-glow 0.8s ease-in-out 5;

    &:hover {
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.22), 0 10px 32px rgba(0, 0, 0, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.6);
        border-color: #ccc;
    }

    @keyframes blink-glow {
        0%,
        100% {
            box-shadow: 0 3px 10px rgba(0, 0, 0, 0.14), 0 8px 24px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.6);
        }
        50% {
            box-shadow: 0 3px 16px rgba(211, 47, 47, 0.4), 0 8px 36px rgba(211, 47, 47, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.6);
            border-color: #ffaaaa;
        }
    }
`;

const CloseContainer = styled.div`
    grid-column: 2;
    grid-row: 1;
    display: flex;
    align-items: flex-start;
    justify-content: center;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    font-size: 24px;
    color: #999;
    padding: 0;
    line-height: 1;
    flex-shrink: 0;
    &:hover {
        color: #333;
    }
`;

const AlertContent = styled.div`
    grid-column: 1;
    grid-row: 2;
`;

const AlertTitle = styled.p`
    font-size: 15px;
    font-weight: bold;
    margin: 0 0 4px 0;
    color: #b71c1c;
`;

const AlertMessage = styled.p`
    font-size: 13px;
    margin: 0;
`;

interface ServiceInfo {
    key: string;
    label: string;
    credit: number;
    autoRefill: boolean;
}

const addonsUrl = backofficeUrl('/User/?tab=subscriptions#addons-title');

const CREDITS_QUERY = `
	query creditsAlertQuery {
		vectorizeCredit: serviceCredit(serviceName: VECTORIZE) {
			credit
			autoRefill
			unlimitedCredits
			hasSellerPurchasedCredits
		}
		aiImageCredit: serviceCredit(serviceName: GENERATIVE_AI) {
			credit
			autoRefill
			unlimitedCredits
			hasSellerPurchasedCredits
		}
	}
`;

const CreditsAlert = () => {
    const [services, setServices] = useState<ServiceInfo[]>([]);

    const fetchCredits = () => {
        fetch(GRAPHQL_URL, {
            method: 'POST',
            headers: getHeaders() as HeadersInit,
            body: JSON.stringify({ query: CREDITS_QUERY, variables: {} })
        })
            .then((res) => res.json())
            .then((result) => {
                const data = result?.data;
                if (!data) return;

                const next: ServiceInfo[] = [];

                if (
                    data.vectorizeCredit &&
                    !data.vectorizeCredit.unlimitedCredits &&
                    data.vectorizeCredit.credit !== null &&
                    Number(data.vectorizeCredit.credit) <= 1.2
                ) {
                    next.push({
                        key: 'VECTORIZE',
                        label: 'AI Vectorizer',
                        credit: Number(data.vectorizeCredit.credit),
                        autoRefill: data.vectorizeCredit.autoRefill
                    });
                } else {
                    localStorage.removeItem(STORAGE_KEY_PREFIX + 'VECTORIZE');
                }

                if (
                    data.aiImageCredit &&
                    !data.aiImageCredit.unlimitedCredits &&
                    data.aiImageCredit.credit !== null &&
                    Number(data.aiImageCredit.credit) <= 1.2
                ) {
                    next.push({
                        key: 'GENERATIVE_AI',
                        label: 'AI Creative Tools',
                        credit: Number(data.aiImageCredit.credit),
                        autoRefill: data.aiImageCredit.autoRefill
                    });
                } else {
                    localStorage.removeItem(STORAGE_KEY_PREFIX + 'GENERATIVE_AI');
                }

                setServices(next);
            })
            .catch(() => {
                // Non-critical — silently ignore errors
            });
    };

    const lastFetchRef = useRef(0);
    const fetchIfThrottleAllows = () => {
        const now = Date.now();
        if (now - lastFetchRef.current < REFRESH_THROTTLE_MS) return;
        lastFetchRef.current = now;
        fetchCredits();
    };

    useEffect(() => {
        lastFetchRef.current = Date.now();
        fetchCredits();
        const onRestored = () => {
            lastFetchRef.current = Date.now();
            fetchCredits();
        };
        window.addEventListener('creditsRestored', onRestored);
        return () => window.removeEventListener('creditsRestored', onRestored);
    }, []);

    const visibleServices = services.filter((s) => !isDismissed(s.key, s.credit));

    const [dismissed, setDismissed] = useState<string[]>([]);
    const [toolbarBottom, setToolbarBottom] = useState(65);

    useEffect(() => {
        const updatePosition = () => {
            const toolbar = document.getElementById('toolbar');
            if (toolbar) {
                const rect = toolbar.getBoundingClientRect();
                setToolbarBottom(rect.bottom + 10);
            }
        };

        updatePosition();

        const toolbar = document.getElementById('toolbar');
        let resizeObserver: ResizeObserver | null = null;
        if (toolbar) {
            resizeObserver = new ResizeObserver(updatePosition);
            resizeObserver.observe(toolbar);
        }

        // Fallback: check periodically for the first few seconds in case the toolbar content loads late
        const intervals = [500, 1000, 2000, 4000];
        const timers = intervals.map((ms) => setTimeout(updatePosition, ms));

        window.addEventListener('resize', updatePosition);
        return () => {
            resizeObserver?.disconnect();
            timers.forEach(clearTimeout);
            window.removeEventListener('resize', updatePosition);
        };
    }, []);

    const handleDismiss = (service: ServiceInfo) => {
        dismiss(service.key, service.credit);
        setDismissed((prev) => [...prev, service.key]);
    };

    const location = useLocation();

    useEffect(() => {
        const matchedRoute = routes.find((r) => {
            const urlWithoutOptional = r.url.replace(/:\w+\?/g, '');
            return matchPath(r.url.replaceAll('?', ''), location.pathname) || matchPath(urlWithoutOptional, location.pathname + '/');
        });
        if (matchedRoute?.refreshCredits) fetchIfThrottleAllows();
    }, [location.pathname]);

    const alertsToShow = visibleServices.filter((s) => !dismissed.includes(s.key));

    if (alertsToShow.length === 0) return null;

    return (
        <AlertOverlay $top={toolbarBottom}>
            {alertsToShow.map((service) => (
                <AlertBubble key={`${service.key}-${location.pathname}`}>
                    <AlertContent>
                        <AlertTitle>Out of credits for {service.label}.</AlertTitle>
                        <AlertMessage>
                            <br />
                            <br />
                            As a result, <strong>the tool is currently not working in your store’s Customizer UI for end customers.</strong>
                            <br />
                            <br />
                            To restore it, purchase additional credits on{' '}
                            <StrongAlertContent>
                                {'My Account > Addons > '}
                                <Link href={backofficeUrl('/User/?tab=subscriptions#addons-title')} target={'_blank'}>
                                    {service.label}
                                </Link>
                            </StrongAlertContent>{' '}
                            section.
                            {!service.autoRefill && (
                                <>
                                    <br /> <br />
                                    You can also enable email notifications to be alerted when your credits drop below 20%.
                                </>
                            )}
                        </AlertMessage>
                    </AlertContent>
                    <CloseContainer>
                        <CloseButton onClick={() => handleDismiss(service)} aria-label='Close'>
                            &times;
                        </CloseButton>
                    </CloseContainer>
                </AlertBubble>
            ))}
        </AlertOverlay>
    );
};

export default CreditsAlert;
